import { Text, Spinner, Tag } from "@ttc3k/trekker";
import { Center, Flex } from "styled-system/jsx";
import {
  SearchInput,
  filterOptions,
  FilterScreenProps,
  ListFilterCard,
} from "shared/components";
import { EnumListType } from "gql/generated";
import { useTranslation } from "react-i18next";
import { ListFilters } from "./useFilters";
import { useGetTagsAndTagGroupsByListFilters } from "apps/customLists/hooks";

type AllFiltersProps = Omit<FilterScreenProps, "currentScreenFilterOption"> & {
  appliedFilters: ListFilters;
  search: string;
  onUpdateSearch: (value: string) => void;
};

export const AllFilters = ({
  onScreenChange,
  type,
  appliedFilters,
  search,
  onUpdateSearch,
}: AllFiltersProps) => {
  const { t } = useTranslation();

  const { tags, tagGroups, error, loading } =
    useGetTagsAndTagGroupsByListFilters(appliedFilters);

  if (error) {
    throw new Error("Error loading tags or tagGroups");
  }

  const numOfAppliedFilters = Object.values({
    ...appliedFilters,
    // Remove filledTagGroups from count
    filledTagGroups: null,
  }).reduce((acc, filter) => {
    if (typeof filter === "string" || Array.isArray(filter)) {
      // If the filter item has a length greater than 0, increase the counter
      // by 1 per filter item
      return acc + (filter.length > 0 ? 1 : 0);
    } else if (filter) {
      // Check each of the tags/location filters if they have a length greater than 0,
      // increase the counter by 1 per tag/location filter item
      const numOfTypesWithTagLocationApplied = Object.values(filter).reduce(
        (acc, arr) => {
          // Return 0 if acc or arr is undefined
          if (acc === undefined || arr === undefined) return 0;
          return acc + (arr.length > 0 ? 1 : 0);
        },
        0, // Initialize acc to 0
      );
      return acc + numOfTypesWithTagLocationApplied;
    } else {
      return acc;
    }
  }, 0);

  const FiltersTag = (
    <Tag
      visual={numOfAppliedFilters > 0 ? "accent.blue" : "primary"}
      mode={"light"}
    >
      {t("core:FILTER.COUNT", {
        count: numOfAppliedFilters,
      })}
    </Tag>
  );

  return (
    <>
      <Flex flexDir={"column"} gap={"100"}>
        <Flex
          justifyContent={"space-between"}
          gap={"200"}
          alignItems={"center"}
        >
          <Text visual={"bodySemiBold"} color={"text.dark"}>
            {t(`customLists:LIST.CREATE_NEW.${type}.FILTERS.TITLE`)}
          </Text>
          {type === EnumListType.Smart && FiltersTag}
        </Flex>
        <Text visual={"smallMedium"} color={"text.mid"}>
          {t(`customLists:LIST.CREATE_NEW.${type}.FILTERS.DESCRIPTION`)}
        </Text>
      </Flex>
      {type === EnumListType.Manual && (
        <>
          <SearchInput
            placeholder={t("core:SEARCH")}
            value={search}
            onChange={(e) => onUpdateSearch(e.target.value)}
            onClearSearch={() => onUpdateSearch("")}
          />
          <Flex
            justifyContent={"space-between"}
            gap={"200"}
            alignItems={"center"}
          >
            <Text visual={"smallSemiBold"} color={"text.mid"}>
              {t(`customLists:LIST.CREATE_NEW.${type}.FILTERS.SUBTITLE`)}
            </Text>
            {FiltersTag}
          </Flex>
        </>
      )}
      <Flex flexDir={"column"} gap={"150"}>
        {/* If any queries are loading, show the loading spinner */}
        {loading ? (
          <Center h={"full"}>
            <Spinner w={"40px"} h={"40px"} />
          </Center>
        ) : (
          <>
            {filterOptions.map((filter) => (
              <ListFilterCard
                key={filter.key}
                filter={filter}
                onScreenChange={onScreenChange}
                appliedFilters={appliedFilters}
                tags={tags ?? []}
                tagGroups={tagGroups ?? []}
              />
            ))}
          </>
        )}
      </Flex>
    </>
  );
};
